import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, CheckCircle, AlertCircle, Loader2, Calendar, Tag } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { PRODUCTS } from '../stripe-config';
import { cn } from '../utils/cn';

interface SubscriptionData {
  subscription_status: string;
  current_period_end: number | null;
  price_id: string | null;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

const SubscriptionPage: React.FC = () => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const { isAuthenticated, checkSubscriptionStatus } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('❌ セッション取得失敗:', sessionError.message);
          navigate('/login');
          return;
        }

        if (!session) {
          console.log('❌ セッションなし');
          navigate('/login');
          return;
        }

        console.log('✅ セッション取得成功');

        await checkSubscriptionStatus();

        if (!isAuthenticated) {
          navigate('/login');
          return;
        }
        
        await fetchSubscription();
      } catch (err) {
        console.error('セッション取得エラー:', err);
        navigate('/login');
      }
    };

    checkAuthAndFetchData();
  }, [navigate, checkSubscriptionStatus, isAuthenticated]);
    
  const fetchSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('stripe_user_subscriptions')
        .select('subscription_status, current_period_end, price_id, payment_method_brand, payment_method_last4')
        .maybeSingle();
      
      if (error) throw error;
      
      setSubscription(data);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('サブスクリプション情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId: string, mode: 'subscription' | 'payment') => {
    try {
      setCheckoutLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('認証エラー: セッションが見つかりません');
      }

      const functionUrl = new URL('/functions/v1/stripe-checkout', import.meta.env.VITE_SUPABASE_URL).toString();
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          price_id: priceId,
          success_url: `${window.location.origin}/subscription?success=true`,
          cancel_url: `${window.location.origin}/subscription?canceled=true`,
          mode,
          promotion_code: 'promo_1Rm9h6CEgfoZyV3UKPwNs6bK',
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'チェックアウトの作成に失敗しました' }));
        throw new Error(errorData.error || 'チェックアウトの作成に失敗しました');
      }
      
      const { url } = await response.json();
      
      if (!url) {
        throw new Error('チェックアウトURLが見つかりません');
      }

      window.location.href = url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err instanceof Error ? err.message : 'チェックアウトの作成に失敗しました');
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setCheckoutLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('認証エラー: セッションが見つかりません');
      }

      const functionUrl = new URL('/functions/v1/stripe-portal', import.meta.env.VITE_SUPABASE_URL).toString();
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          return_url: `${window.location.origin}/subscription`,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'カスタマーポータルの作成に失敗しました' }));
        throw new Error(errorData.error || 'カスタマーポータルの作成に失敗しました');
      }
      
      const { url } = await response.json();
      
      if (!url) {
        throw new Error('カスタマーポータルURLが見つかりません');
      }

      window.location.href = url;
    } catch (err) {
      console.error('Error creating customer portal session:', err);
      setError('現在、サブスクリプション管理機能は準備中です。サポートにお問い合わせください。');
      setCheckoutLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    
    if (success === 'true') {
      checkSubscriptionStatus();
      fetchSubscription();
    }
  }, [checkSubscriptionStatus]);

  const isActiveSubscription = subscription?.subscription_status === 'active' || 
                              subscription?.subscription_status === 'trialing';

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '不明';
    return new Date(timestamp * 1000).toLocaleDateString('ja-JP');
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'アクティブ';
      case 'trialing': return 'トライアル期間中';
      case 'past_due': return '支払い遅延';
      case 'canceled': return 'キャンセル済み';
      case 'unpaid': return '未払い';
      case 'not_started': return '未登録';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-700 bg-green-100';
      case 'trialing': return 'text-blue-700 bg-blue-100';
      case 'past_due': return 'text-orange-700 bg-orange-100';
      case 'canceled': return 'text-red-700 bg-red-100';
      case 'unpaid': return 'text-red-700 bg-red-100';
      case 'not_started': return 'text-gray-700 bg-gray-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const urlParams = new URLSearchParams(window.location.search);
  const success = urlParams.get('success');
  const canceled = urlParams.get('canceled');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-lg text-gray-600">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">サブスクリプション管理</h1>
          <button
            onClick={() => navigate('/')}
            className="btn btn-secondary flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            戻る
          </button>
        </div>

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start">
            <CheckCircle className="h-5 w-5 mr-2 mt-0.5" />
            <div>
              <p className="font-medium">サブスクリプションの登録が完了しました</p>
              <p className="text-sm">ご登録ありがとうございます。アプリの全機能をご利用いただけます。</p>
            </div>
          </div>
        )}

        {canceled && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 mt-0.5" />
            <div>
              <p className="font-medium">サブスクリプションの登録がキャンセルされました</p>
              <p className="text-sm">お支払いプロセスがキャンセルされました。再度お試しください。</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 mt-0.5" />
            <div>
              <p className="font-medium">エラーが発生しました</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">現在のサブスクリプション</h2>
            
            {subscription ? (
              <div className="space-y-4">
                <div className="flex items-center">
                  <span className="text-gray-600 w-32">ステータス:</span>
                  <span className={cn(
                    "px-2 py-1 rounded-full text-sm font-medium",
                    getStatusColor(subscription.subscription_status)
                  )}>
                    {getStatusText(subscription.subscription_status)}
                  </span>
                </div>
                
                {subscription.current_period_end && (
                  <div className="flex items-center">
                    <span className="text-gray-600 w-32">次回更新日:</span>
                    <span className="text-gray-900 flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(subscription.current_period_end)}
                    </span>
                  </div>
                )}
                
                {subscription.payment_method_brand && subscription.payment_method_last4 && (
                  <div className="flex items-center">
                    <span className="text-gray-600 w-32">支払い方法:</span>
                    <span className="text-gray-900 flex items-center">
                      <CreditCard className="h-4 w-4 mr-1" />
                      {subscription.payment_method_brand} **** {subscription.payment_method_last4}
                    </span>
                  </div>
                )}
                
                {isActiveSubscription && (
                  <div className="mt-6">
                    <button
                      onClick={handleManageSubscription}
                      disabled={checkoutLoading}
                      className="btn btn-secondary"
                    >
                      {checkoutLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          読み込み中...
                        </>
                      ) : (
                        'サブスクリプションを管理する'
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-600">サブスクリプション情報がありません</p>
            )}
          </div>

          {(!isActiveSubscription || subscription?.subscription_status === 'canceled') && (
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">利用可能なプラン</h2>
              
              <div className="space-y-6">
                {PRODUCTS.map((product) => (
                  <div key={product.priceId} className="border border-gray-200 rounded-lg p-6 hover:border-primary-300 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{product.name}</h3>
                        <p className="text-gray-600 mt-1">{product.description}</p>
                        <div className="mt-2 flex items-center">
                          <Tag className="h-4 w-4 text-green-600 mr-1" />
                          <span className="text-green-600 font-medium">初回登録から3ヶ月無料</span>
                        </div>
                        <p className="text-gray-500 text-sm mt-2">その後、年額500円（税込）</p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => handleSubscribe(product.priceId, product.mode)}
                          disabled={checkoutLoading}
                          className="btn btn-primary"
                        >
                          {checkoutLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              読み込み中...
                            </>
                          ) : (
                            '登録する'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-700 text-sm">
                  年額500円のサブスクリプションですが、初回登録から3ヶ月間は無料でご利用いただけます。
                  登録後にお支払いページでお決済いただくと、3ヶ月後から年額プランが開始されます。
                </p>
                <div className="mt-2 text-xs text-blue-600">
                  <ul className="list-disc list-inside space-y-1">
                    <li>初回3ヶ月：完全無料</li>
                    <li>4ヶ月目以降：年額500円（月額約41円）</li>
                    <li>いつでもキャンセル可能</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;